import { useState, useEffect, useCallback, cloneElement } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useParams } from 'react-router-dom';
import parse from 'html-react-parser';
import { useFormik } from 'formik';
import { Swal } from '@utils/alerts';
import moment from 'moment';
import { useCatalogsPublic } from '@hooks/useCatalogsPublic';

// Material UI
import {
  Container,
  Grid,
  Box,
  Typography,
  Card,
  AppBar,
  Toolbar,
  Button,
  Stack,
  Skeleton,
  Icon,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import CssBaseline from '@mui/material/CssBaseline';
import { AppRegistration, Home, Route } from '@mui/icons-material';

// Componentes
import SearchPeople from '@components/Events/SearchPeople';
import PreguntasRespuestasEventos from '@components/Events/PreguntasRespuestasEventos';
import QRPreregistro from '@components/Events/QRPreregistro';
import FormRegister from '@components/Events/FormRegister';
import CustomModal from '@components/Modal/CustomModal';

// Utils
import { isNullOrUndefined, isEmptyOrNullObject } from '@utils/validations';
import {
  validaINE,
  handleAttrs,
  validaCURP,
  handleFormikSubmit,
  formatDateAPI,
  downloadFileByURL,
} from '@utils/Utilities';
import { RECAPTCHA_SITE_KEY } from '@utils/global';

// Servicios
import EventsServices from '@services/EventsServices';

// Data
import { CompromisoEventSchema, CompromisoBasicEventExternoSchema } from '@schemas/EventosSchemas';
import {
  CompromisoEventInterface,
  CompromisoBasicEventExternoInterface,
} from '@interfaces/EventosInterfaces';
import { ID_FOCA } from '@data/constants/Eventos';

// Assets
import eventosImg from '@assets/img/eventosImg.jpg';
import foca_IMG from '@assets/img/eventosImg.jpg';

function ElevationScroll(props) {
  const { children, window } = props;

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
    target: window ? window() : undefined,
  });

  return cloneElement(children, { elevation: trigger ? 4 : 0 });
}

const PreRegistro = ({ type }) => {
  const catalogsParams = [
    { id: 'entidades' },
    { id: 'municipios' },
    { id: 'tipo_vialidad' },
    { id: 'generos' },
    { id: 'grados_estudios' },
    { id: 'ocupacion_laboral' },
    { id: 'voluntarios' },
  ];
  const isFOCA = type === ID_FOCA;

  const { id } = useParams();

  const [isMobile, setIsMobile] = useState(false);
  const [dominantColor, setDominantColor] = useState('#00000');

  const [initialValues, setInitialValues] = useState(
    CompromisoEventInterface({ idEvento: '', type, idModoEvento: 2 })
  );
  const [validationSchema, setValidationSchema] = useState(CompromisoEventSchema());

  const [loading, setLoading] = useState(true);
  const [dataEvent, setDataEvent] = useState({});
  const [dataPreguntas, setDataPreguntas] = useState([]);
  const [preguntasArchivos, setPreguntasArchivos] = useState([]);
  const [convocatoriaArchivos, setConvocatoriaArchivos] = useState([]);
  const [flagQuestion, setFlagQuestion] = useState(false);

  const [datauser, setDataUser] = useState({});
  const [flagNot, setFlagNot] = useState(false);
  const [loadingRegister, setLoadingRegister] = useState(false);

  const [dataQR, setDataQR] = useState({});
  const [flagQR, setFlagQR] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [LoadingForm, setLoadingForm] = useState(false);

  const { catalogs, load } = useCatalogsPublic({
    catalogsOptions: catalogsParams,
    putDefaultOption: false,
  });
  const { executeRecaptcha } = useGoogleReCaptcha();

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (parseInt(dataEvent.idModoEvento) === 3) {
        values = {
          ...values,
          account: { Password: '12345678', Confirm: '12345678', PIN: '12345' },
        };
      }

      let newValues = { ...values };

      if (parseInt(dataEvent.idModoEvento) === 4) {
        delete newValues.extras.idUsuarioPropietario;
        delete newValues.contact.TelMensajes;
        newValues.account.Username = newValues.contact.Celular;

        handleSave(newValues);
      } else {
        onChangeCaptcha(handleAttrs(newValues));
      }
    },
  });

  useEffect(() => {
    getEventos(); // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!isEmptyOrNullObject(dataEvent)) {
      // Cambiar esquema e interfaz según idModoEvento
      if (dataEvent.idModoEvento === 4) {
        setInitialValues(CompromisoBasicEventExternoInterface(dataEvent.id));
        setValidationSchema(CompromisoBasicEventExternoSchema);
      } else {
        const { id: idEvento, idModoEvento, soloMenores } = dataEvent;
        const isMenor = soloMenores;
        setInitialValues(CompromisoEventInterface({ idEvento, type, idModoEvento, isMenor }));
        setValidationSchema(CompromisoEventSchema(type, idModoEvento));
      }
    } // eslint-disable-next-line
  }, [dataEvent]);

  useEffect(() => {
    const userAgent = navigator.userAgent;
    const mobileRegex = /Mobi/i;
    const isMobileDevice = mobileRegex.test(userAgent);
    setIsMobile(isMobileDevice);

    const imageUrl = isFOCA
      ? foca_IMG
      : isMobileDevice
      ? dataEvent.imgMovilURL ?? eventosImg
      : dataEvent.imgURL ?? eventosImg;

    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.src = imageUrl;

    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0);

      const imageData = context.getImageData(0, 0, canvas.width, canvas.height).data;
      let totalR = 0;
      let totalG = 0;
      let totalB = 0;
      const totalPixels = imageData.length / 4;

      for (let i = 0; i < imageData.length; i += 4) {
        totalR += imageData[i];
        totalG += imageData[i + 1];
        totalB += imageData[i + 2];
      }

      const avgR = Math.floor(totalR / totalPixels);
      const avgG = Math.floor(totalG / totalPixels);
      const avgB = Math.floor(totalB / totalPixels);

      const rgbToHex = (r, g, b) => {
        const componentToHex = (c) => {
          const hex = c.toString(16);
          return hex.length === 1 ? '0' + hex : hex;
        };
        return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
      };

      const hexColor = rgbToHex(avgR, avgG, avgB);
      setDominantColor(hexColor);
    };
    // eslint-disable-next-line
  }, [dataEvent, navigator.userAgent]);

  const getEventos = async () => {
    try {
      setLoading(true);

      const result = await EventsServices.getPublicEvent({ uuid: id, type });
      const { results, response, message } = result;

      if (results) {
        const event = response.data;

        const localParams = {
          page: 0,
          pageSize: 10,
          filtered: [
            { id: 'idEvento', filter: '=', value: event.id },
            { id: 'idTipoPregunta', filter: '=', value: 1 },
          ],
        };

        const result2 = await EventsServices.getQuestions(localParams);
        const { results: results2, response: response2, message: message2 } = result2;

        const localParams3 = {
          page: 0,
          pageSize: 10,
          filtered: [{ id: 'idEvento', filter: '=', value: event.id }],
        };

        const result3 = await EventsServices.getConvocatorias(localParams3);
        const { results: results3, response: response3, message: message3 } = await result3;

        if (results2 && results3) {
          formik.setFieldValue('extras.idUsuarioPropietario', event.idUsuarioCreo, false);
          formik.setFieldValue('idEvento', event.id, false);
          formik.setFieldValue('idModoEvento', event.idModoEvento, false);

          if (parseInt(event.idModoEvento) === 3) {
            formik.setFieldValue('account.Password', '12345678', false);
            formik.setFieldValue('account.Confirm', '12345678', false);
            formik.setFieldValue('account.PIN', '12345', false);
          }

          setDataEvent(event);
          setDataPreguntas(response2.data || []);
          setConvocatoriaArchivos(response3.data || []);

          if (response2.data?.length) {
            const respuestasConArchivos = response2.data
              .filter((r) => r.idTipoRespuesta === 4)
              .map((r) => r.Respuestas[0]?.Respuesta)
              .filter((r) => r.fileURL);

            setPreguntasArchivos(respuestasConArchivos);
          }
        } else throw new Error(results2 ? message3 : message2);
      } else throw new Error(message);
    } catch (e) {
      Swal.fire({ title: e.message, icon: 'warning' });
      setDataEvent({});
      setDataPreguntas([]);
      setConvocatoriaArchivos([]);
      setPreguntasArchivos([]);
    } finally {
      setLoading(false);
    }
  };

  const RegisterEvent = async (answers) => {
    if (!datauser.id) {
      Swal.fire({ title: 'Hace falta su clave de elector', icon: 'warning' });
      return true;
    }

    if (dataPreguntas.length !== 0 && answers.length === 0 && datauser.pedir_cuestionario) {
      Swal.fire({ title: 'Hace falta contestar alguna pregunta del formulario.', icon: 'warning' });
      return true;
    }

    if (loadingRegister) return false;

    setLoadingRegister(true);

    const localParams = {
      idUsuarioInvitado: datauser.id,
      idEvento: dataEvent.id,
      answers,
      correo: datauser.Correo,
      celular: datauser.Celular,
      nombre: datauser.Nombre,
      paterno: datauser.Paterno,
      materno: datauser.Materno,
    };

    try {
      const result = await EventsServices.publicSetGuestEvent(localParams);
      const { results, response, message } = result;

      if (results) {
        setFlagQR(true);
        setMensaje(message);
        setDataQR(response.data);
        setFlagQuestion(false);
      } else throw new Error(message);
    } catch (e) {
      Swal.fire({ title: e.message, icon: 'warning' });
    } finally {
      setLoadingRegister(false);
    }
  };

  const RegisterEventWithoutQuestions = async (answers) => {
    if (!datauser.id) {
      Swal.fire({ title: 'Hace falta su clave de elector', icon: 'warning' });
      return true;
    }

    if (loadingRegister) return false;

    setLoadingRegister(true);

    const localParams = {
      idUsuarioInvitado: datauser.id,
      idEvento: dataEvent.id,
      answers,
      correo: datauser.Correo,
      celular: datauser.Celular,
      nombre: datauser.Nombre,
      paterno: datauser.Paterno,
      materno: datauser.Materno,
    };

    try {
      const result = await EventsServices.publicSetGuestEvent(localParams);
      const { results, response, message } = result;

      if (results) {
        setFlagQR(true);
        setMensaje(message);
        setDataQR(response.data);
      } else throw new Error(message);
    } catch (e) {
      Swal.fire({ title: e.message, icon: 'warning' });
    } finally {
      setLoadingRegister(false);
      setFlagQuestion(false);
    }
  };

  const onChangeCaptcha = useCallback(
    (values) => {
      if (!executeRecaptcha) return;

      executeRecaptcha('eventos')
        .then((token) => {
          const data = { ...values, token: token };
          handleVerification(handleAttrs(data));
        })
        .catch((error) => Swal.fire({ title: error, icon: 'warning' }));
    },
    // eslint-disable-next-line
    [executeRecaptcha]
  );

  const handleVerification = (values) => {
    if (!isEmptyOrNullObject(values)) {
      let res = { results: false, message: 'Datos no validados' };
      if (dataEvent.idModoEvento !== 4) {
        if (values.user.isMenor) {
          res = validaCURP(values.user.CURP, values.user.Nombre, values.user.Paterno, values.user.Materno);
        } else {
          res = validaINE(
            values.user.INE,
            values.user.Nombre,
            values.user.Paterno,
            values.user.Materno,
            values.user.idSexo,
            values.user.FechaNacimiento,
            values.user.idEstadoNacimiento
          );
        }
      } else res.results = true;

      if (!res.results) {
        Swal.fire({
          title: `¡Atención!`,
          text: res.message,
          icon: 'info',
          reverseButtons: true,
          showCancelButton: false,
          confirmButtonText: 'Ok',
        });
      } else handleSave(values);
    }
  };

  const handleSave = async (values) => {
    if (LoadingForm) return false;

    const isSencillo = values.idModoEvento === 4;

    setLoadingForm(true);
    let data = {};

    if (!isSencillo) {
      const interfaz = CompromisoEventInterface({
        idEvento: dataEvent.id,
        type,
        idModoEvento: values.idModoEvento,
      });

      Object.keys(interfaz).forEach((key) => {
        data[key] = values[key] !== null ? values[key] : interfaz[key];
      });

      data = {
        ...data,
        user: {
          ...data.user,
          CURP: values.user.CURP ? values.user.CURP?.toUpperCase() : '',
          INE: values.user.INE ? values.user.INE?.toUpperCase() : '',
          VigenciaINE: values.user.VigenciaINE ? parseInt(values.user.VigenciaINE) : null,
          SeccionVota: values.user.isMenor === 1 ? null : values.user.SeccionVota,

          Nombre: values.user.Nombre?.toUpperCase(),
          Paterno: values.user.Paterno?.toUpperCase(),
          Materno: values.user.Materno?.toUpperCase(),
          FechaNacimiento: moment(values.user.FechaNacimiento).format('YYYY-MM-DD'),
        },

        ...(isFOCA
          ? {
              formation: {
                ...data.formation,
                initialDate: formatDateAPI(values.formation.initialDate),
                finalDate: formatDateAPI(values.formation.finalDate),
              },
            }
          : {}),

        address: {
          ...data.address,
          CalleVive: `${values.address.TipoVialidad} ${values.address.CalleVive}`,
        },

        account: {
          ...data.account,
          Username: values.contact.Celular,
        },

        extras: {
          ...data.extras,
          medioConfirmacion: 'sms',
          idEstructuraSocial: 1,
        },

        EsMenor: values.user.isMenor ? 1 : 0,
      };

      if (values.idModoEvento === 3) {
        delete data.academy;
        delete data.employment;
        delete data.formation;
        delete data.invitation;
      }

      delete data.address.TipoVialidad;
      delete data.account.Confirm;
      delete data.user.isMenor;

      if (values.idModoEvento === 3 && dataPreguntas.length !== 0 && data.answers.length === 0) {
        Swal.fire({ title: 'Hace Falta, contestar una pregunta del formulario.', icon: 'warning' });
        return true;
      }
    } else {
      data = values;
      delete data.answers;
    }

    const api = !isSencillo ? EventsServices.publicRegisterGuest : EventsServices.publicRegisterGuestSencillo;

    try {
      const result = await api(data);
      const { success, results, response, message } = result;

      if (success && results) {
        if (response?.data?.filtered) {
          const result2 = await EventsServices.publicGetUserEvent(response.data);
          const { results: results2, response: response2, message: message2 } = result2;

          if (results2) {
            setFlagQuestion(false);
            setFlagQR(true);
            setMensaje(message);
            setDataQR(response2.data);
            clearForm();
          } else throw new Error(message2);
        } else {
          setFlagQuestion(false);
          setFlagQR(true);
          setMensaje(message);
          setDataQR(response.data);
          clearForm();
        }
      } else throw new Error(message);
    } catch (e) {
      Swal.fire({ title: e.message, icon: 'warning' });
    } finally {
      setLoadingForm(false);
    }
  };

  const clear = () => {
    setFlagNot(false);
    setFlagQR(false);
    setDataUser({ message: '' });
    setDataQR({});
    setFlagQuestion(false);

    formik.resetForm({});
    formik.setFieldValue('extras.idUsuarioPropietario', dataEvent.idUsuarioCreo, false);
    formik.setFieldValue('idEvento', dataEvent.id, false);
  };

  const clearForm = () => {
    formik.resetForm({});
    formik.setFieldValue('extras.idUsuarioPropietario', dataEvent.idUsuarioCreo, false);
    formik.setFieldValue('idEvento', dataEvent.id, false);
  };

  const handleAttendence = (values) => {
    formik.setFieldValue('answers', values);

    if (!flagNot && flagQuestion) RegisterEvent(values);

    if (flagNot && !flagQR) handleFormikSubmit(formik);
  };

  const [isDownloading, setIsDownloading] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const handleDownload = async ({ id }) => {
    try {
      setIsDownloading(true);

      const result = await EventsServices.downloadCroquis({ id });

      const { success, response } = result;

      if (!success || !response || !response.data || response.data.length === 0) {
        throw new Error('No se encontró la imagen');
      }

      // Obtén la URL de la imagen desde la respuesta
      const imageUrl = response.data[0].imageURL;

      if (imageUrl) {
        // Hacer la solicitud para obtener la imagen como blob
        const imageBlob = await fetch(imageUrl).then((res) => res.blob());

        // Crear un enlace de descarga con un blob
        const downloadLink = document.createElement('a');
        const url = window.URL.createObjectURL(imageBlob);
        downloadLink.href = url;
        downloadLink.download = 'croquis.jpg';
        downloadLink.click();

        // Limpiar el objeto URL creado para evitar pérdidas de memoria
        window.URL.revokeObjectURL(url);
      } else {
        throw new Error('Imagen no disponible');
      }
    } catch (error) {
      Swal.fire({ title: error.message, icon: 'error' });
    } finally {
      setIsDownloading(false);
    }
  };

  const flags = {
    busqueda: !flagNot && !flagQR && !flagQuestion,
    registro: flagNot && !flagQR,
    preguntas: flagQuestion,
    qr: flagQR,
    boton: flagNot && !flagQR && !flagQuestion,
    noRegistro: dataEvent.puedeRegistrarse === 0,
  };

  const flags_archivos = {
    preguntas: (flags.busqueda || flags.registro || flags.preguntas) && preguntasArchivos.length > 0,
    convocatorias: flags.busqueda && convocatoriaArchivos.length > 0,
  };

  // Estado para almacenar la URL de la imagen
  return (
    <>
      <CssBaseline />
      <ElevationScroll>
        <AppBar position="fixed" sx={{ backgroundColor: dominantColor }}>
          <Toolbar>
            {!flagNot && !flagQR && (
              <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                {dataEvent ? dataEvent.Actividad : 'Eventos'}
              </Typography>
            )}
            {(flagNot || flagQR) && (
              <Button
                variant="outlined"
                onClick={clear}
                sx={{ border: 0, p: 0 }}
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                startIcon={<Home />}
              >
                Inicio
              </Button>
            )}
          </Toolbar>
        </AppBar>
      </ElevationScroll>

      {loading ? (
        <Box sx={{ p: 2 }}>
          <Skeleton
            animation="wave"
            sx={{ bgcolor: 'aliceblue' }}
            variant="rectangular"
            width="100%"
            height="calc(100vh - 32px)"
          />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              minHeight: '60vh',
              width: '100%',
              backgroundImage: isFOCA
                ? `url(${foca_IMG})`
                : Object.entries(dataEvent).length !== 0 &&
                  isNullOrUndefined(isMobile ? dataEvent : dataEvent.imgURL)
                ? `url(${eventosImg})`
                : isMobile
                ? `url(${dataEvent.imgMovilURL})`
                : `url(${dataEvent.imgURL})`,
              backgroundSize: '100%',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                minHeight: '75vh',
                width: '100%',
                backgroundColor:
                  Object.entries(dataEvent).length !== 0 &&
                  isNullOrUndefined(isMobile ? dataEvent.imgMovilURL : dataEvent.imgURL)
                    ? 'rgba(255, 255, 255, 0.5)'
                    : 'rgba(0, 0, 0, 0)', // Color oscuro con una opacidad de 0.5
                zIndex: 1,
              }}
            />

            {Object.entries(dataEvent).length !== 0 && isNullOrUndefined(dataEvent.imgURL) && (
              <Container sx={{ zIndex: 2 }}>
                <Grid
                  container
                  item
                  xs={12}
                  lg={8}
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  sx={{ mx: 'auto', textAlign: 'center' }}
                >
                  {parse(dataEvent.textoPreregistro)}
                </Grid>
              </Container>
            )}
          </Box>

          <Card
            sx={{
              position: 'relative',
              zIndex: 3,
              p: 2,
              mx: { xs: 2, lg: 3 },
              mt: -8,
              mb: 4,
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            }}
          >
            {/* Aquí va el Box para el botón "Ver Croquis" */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              {Object.entries(dataEvent).length !== 0 && !isNullOrUndefined(dataEvent.croquisURL) && (
                // <Button
                //   onClick={() => setOpenModal(true)}
                //   disabled={isDownloading}
                //   sx={{ border: '1px solid', padding: '8px 16px' }}
                // >
                //   Ver Croquis
                // </Button>

                <Button
                  onClick={() => setOpenModal(true)}
                  disabled={isDownloading}
                  variant="outlined"
                  sx={{ ml: 'auto' }} // Alinea el botón a la derecha
                  endIcon={<Route />} // Coloca el ícono al final del botón
                >
                  Ver Croquis
                </Button>
              )}
            </Box>

            {(flags_archivos.convocatorias || flags_archivos.preguntas) && (
              <Grid
                container
                spacing={2}
                justifyContent="center"
                alignItems="flex-start"
                sx={{ mb: flags.preguntas ? 3 : 0 }}
              >
                {flags_archivos.convocatorias && (
                  <Grid item xs={12} lg={flags_archivos.preguntas ? 6 : 12}>
                    <Typography variant="h6" align="center" gutterBottom>
                      Bases y Convocatoria
                    </Typography>
                    <Grid container spacing={2} justifyContent="center">
                      {convocatoriaArchivos.map((item, index) => (
                        <Grid item key={index}>
                          <Card
                            sx={{
                              minWidth: 150,
                              textAlign: 'center',
                              p: 2,
                              borderRadius: '12px',
                              boxShadow: 'rgba(149, 157, 165, 0.5) 0px 8px 24px',
                            }}
                          >
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>{item.fileName}</strong>
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => downloadFileByURL({ url: item.fileURL, name: item.fileName })}
                              disabled={isDownloading}
                              variant="outlined"
                              endIcon={<Icon>download</Icon>}
                            >
                              Descargar
                            </Button>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                )}

                {flags_archivos.preguntas && (
                  <Grid item xs={12} lg={flags_archivos.convocatorias ? 6 : 12}>
                    <Typography variant="h6" align="center" gutterBottom>
                      Archivos necesarios para el preregistro
                    </Typography>
                    <Grid container spacing={2} justifyContent="center">
                      {preguntasArchivos.map((item, index) => (
                        <Grid item key={index}>
                          <Card
                            sx={{
                              minWidth: 150,
                              textAlign: 'center',
                              p: 2,
                              borderRadius: '12px',
                              boxShadow: 'rgba(149, 157, 165, 0.5) 0px 8px 24px',
                            }}
                          >
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              Archivo: <strong>{item.idFile}</strong>
                            </Typography>
                            <Button
                              size="small"
                              onClick={() => downloadFileByURL({ url: item.fileURL, name: item.idFile })}
                              disabled={isDownloading}
                              variant="outlined"
                              endIcon={<Icon>download</Icon>}
                            >
                              Descargar
                            </Button>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            )}

            {flags.noRegistro && (
              <>
                <Typography variant="h6" align="center" gutterBottom>
                  No puedes registrarte en este evento, ya paso la fecha del preregistro.
                </Typography>

                <Typography variant="h6" align="center">
                  Solo puedes obtener tu QR si te registraste en el evento previamente.
                </Typography>
              </>
            )}

            {flags.busqueda && (
              <SearchPeople
                formik={formik}
                catalogs={catalogs}
                dataEvent={dataEvent}
                datauser={datauser}
                dataPreguntas={dataPreguntas}
                setFlagNot={setFlagNot}
                setFlagQuestion={setFlagQuestion}
                setDataUser={setDataUser}
                register={() => {
                  if (dataEvent.puedeRegistrarse === 1) RegisterEvent(formik.values?.answers ?? []);
                  else RegisterEventWithoutQuestions(formik.values?.answers ?? []);
                }}
                loadingRegister={loadingRegister}
                onlyMenores={dataEvent?.soloMenores === 1}
              />
            )}

            {flags.registro && (
              <FormRegister
                event={dataEvent}
                formik={formik}
                catalogs={catalogs}
                isLoading={load}
                isFOCA={isFOCA}
                handleReturn={clear}
              />
            )}

            {flags.preguntas && (
              <PreguntasRespuestasEventos
                titulo={dataEvent?.Actividad}
                preguntas={dataPreguntas}
                handleChange={handleAttendence}
                buttonOptions={{ title: 'Registrarme', icon: 'app_registration' }}
                justify="end"
                loading={loadingRegister || LoadingForm}
              />
            )}

            {flags.qr && <QRPreregistro dataQR={dataQR} mensaje={mensaje} dataEvent={dataEvent} />}

            {flags.boton && (
              <Grid item xs={12} sm={12} md={12}>
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={4}>
                  <LoadingButton
                    size="small"
                    color="primary"
                    onClick={() => handleFormikSubmit(formik)}
                    loading={LoadingForm}
                    loadingPosition="end"
                    endIcon={<AppRegistration />}
                    variant="contained"
                  >
                    <span>Registrarme</span>
                  </LoadingButton>
                </Stack>
                <br />
              </Grid>
            )}
          </Card>
        </>
      )}

      <CustomModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        title="CROQUIS"
        image={{ src: dataEvent.croquisURL }}
        displayImage={true}
        actionButtons={[
          {
            title: 'Descargar',
            icon: 'download',
            action: () => handleDownload({ id: dataEvent?.id }),
          },
        ]}
      />
    </>
  );
};

const recaptcha = ({ idTipoEvento }) => {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <PreRegistro type={idTipoEvento} />
    </GoogleReCaptchaProvider>
  );
};

export default recaptcha;
